/**
 * Missouri Home Health Agency State-Specific Diagnostic Questions
 * 
 * REGULATORY FRAMEWORK:
 * - Primary State Regulation: 19 CSR 30-26.010 (Home Health Licensure Rule)
 * - Statutory Authority: RSMo 197.400-197.478 (Home Health Agencies)
 * - Federal Standards (Incorporated by Reference): 42 CFR 484 (Medicare CoPs)
 * - Background Checks: RSMo 660.317, Family Care Safety Registry (RSMo 210.900-210.933)
 * - Dementia Training: RSMo 660.050
 * - Employee Disqualification List: RSMo 660.315
 * 
 * KEY VERIFIED REQUIREMENTS (December 2025):
 * - License term: Annual renewal per 19 CSR 30-26.010(2)(C)
 * - License fee: $600 per 19 CSR 30-26.010(2)(C)(1)(A)
 * - Business location required (not private residence) per 19 CSR 30-26.010(2)(B)(2)
 * - Must offer 2+ skilled services, one must be skilled nursing per RSMo 197.400
 * - Missouri adopts Medicare CoPs (42 CFR 484) by reference per 19 CSR 30-26.010(1)(A)
 * - Survey frequency: Every 12 months for agencies <36 months; every 36 months thereafter
 * - FCSR registration required per RSMo 192.2495
 * - Dementia training required per RSMo 660.050 and 19 CSR 30-26.010(1)(B)
 * 
 * Regulatory Body: Missouri Department of Health and Senior Services (DHSS)
 * Bureau of Home Care and Rehabilitative Standards
 * 
 * Last Verified: December 2025 against Missouri Revisor of Statutes and DHSS sources
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const MISSOURI_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'MO-A1',
    itemId: 'A1',
    stateCode: 'MO',
    title: 'Home Health Agency License Status',
    description: 'Missouri requires all home health agencies to be licensed by DHSS under RSMo 197.400-197.478',
    severity: 'high',
    regulationCode: '19 CSR 30-26.010',
    regulationName: 'Licensure',
    agencyName: 'Missouri Department of Health and Senior Services',
    agencyAbbr: 'DHSS',
    agencyPhone: '(573) 751-6336',
    agencyUrl: 'https://health.mo.gov/safety/homecare/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from DHSS?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Home health agency licenses must be renewed annually per 19 CSR 30-26.010(2)(C). License fee is $600.',
      },
      {
        id: 'q2',
        question: 'What is your license expiration date?',
        inputType: 'date',
        helpText: 'Found on your license certificate. License must be posted in a conspicuous place per RSMo 197.415.',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 45, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your Missouri Home Health Agency license is current and active per 19 CSR 30-26.010.',
      atRisk: 'Your license expires within 90 days. Submit renewal application with $600 fee to DHSS before expiration per 19 CSR 30-26.010(2)(C).',
      nonCompliant: 'Operating a home health agency in Missouri without a valid DHSS license violates RSMo 197.400-197.478 and is a Class A misdemeanor. Contact DHSS immediately at (573) 751-6336.',
      unknown: 'Verify your license status with DHSS Bureau of Home Care at (573) 751-6336.',
    },
    stateNotes: [
      'License renewed annually per 19 CSR 30-26.010(2)(C)',
      'License fee is $600 (nonrefundable)',
      'Must offer 2+ skilled services, one must be skilled nursing',
      'Business location required - cannot operate from private residence',
      'Missouri adopts Medicare CoPs (42 CFR 484) by reference',
    ],
  },

  {
    id: 'MO-A2',
    itemId: 'A2',
    stateCode: 'MO',
    title: 'Business Location Requirements',
    description: 'Missouri requires home health agencies to maintain a business location',
    severity: 'high',
    regulationCode: '19 CSR 30-26.010(2)(B)(2)',
    regulationName: 'Business Location',
    agencyName: 'Missouri Department of Health and Senior Services',
    agencyAbbr: 'DHSS',
    agencyPhone: '(573) 751-6336',
    agencyUrl: 'https://health.mo.gov/safety/homecare/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency maintain a business location that is NOT a private residence?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - dedicated business location' },
          { value: 'no', label: 'No - operating from private residence' },
        ],
        required: true,
        helpText: 'Per 19 CSR 30-26.010(2)(B)(2): "The applicant shall establish a business location (not in a private residence) with established business hours."',
      },
      {
        id: 'q2',
        question: 'Does your business location have established business hours?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Business location meets Missouri requirements per 19 CSR 30-26.010(2)(B)(2).',
      atRisk: 'Establish documented business hours for your location.',
      nonCompliant: 'Operating from a private residence violates 19 CSR 30-26.010(2)(B)(2). Establish a dedicated business location.',
      unknown: 'Verify business location requirements with DHSS.',
    },
    stateNotes: [
      'Business location cannot be a private residence',
      'Must have established business hours',
      'License must be posted in conspicuous place at main office',
    ],
  },

  {
    id: 'MO-A3',
    itemId: 'A3',
    stateCode: 'MO',
    title: 'Secretary of State Registration',
    description: 'Missouri requires home health agencies to register with Secretary of State',
    severity: 'high',
    regulationCode: '19 CSR 30-26.010(2)(A)',
    regulationName: 'Business Entity',
    agencyName: 'Missouri Secretary of State',
    agencyAbbr: 'SOS',
    agencyPhone: '(573) 751-4153',
    agencyUrl: 'https://www.sos.mo.gov/business',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency properly registered with the Missouri Secretary of State?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per 19 CSR 30-26.010(2)(A): agencies must conduct business in their legal name or d/b/a as registered with Secretary of State.',
      },
      {
        id: 'q2',
        question: 'Is your registration in good standing?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Unsure' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Business registration is current with Missouri Secretary of State.',
      atRisk: 'Verify your registration is in good standing with Secretary of State.',
      nonCompliant: 'Agency must be registered with Secretary of State per 19 CSR 30-26.010(2)(A). Register immediately.',
      unknown: 'Verify business registration status.',
    },
    stateNotes: [
      'Must conduct business in legal name or registered d/b/a',
      'Proof of registration required for license application and renewal',
    ],
  },

  {
    id: 'MO-A4',
    itemId: 'A4',
    stateCode: 'MO',
    title: 'Skilled Services Requirement',
    description: 'Missouri requires home health agencies to offer at least two skilled services',
    severity: 'high',
    regulationCode: 'RSMo 197.400',
    regulationName: 'Agency Definition',
    agencyName: 'Missouri Department of Health and Senior Services',
    agencyAbbr: 'DHSS',
    agencyPhone: '(573) 751-6336',
    agencyUrl: 'https://health.mo.gov/safety/homecare/',
    questions: [
      {
        id: 'q1',
        question: 'Which skilled services does your agency provide? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'nursing', label: 'Skilled Nursing (required)' },
          { value: 'pt', label: 'Physical Therapy' },
          { value: 'ot', label: 'Occupational Therapy' },
          { value: 'st', label: 'Speech Therapy' },
          { value: 'msw', label: 'Medical Social Services' },
          { value: 'hha', label: 'Home Health Aide' },
        ],
        required: true,
        helpText: 'Per RSMo 197.400: Must provide 2+ skilled services, one of which must be skilled nursing.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['nursing'] },
        { condition: 'countAtLeast', questionId: 'q1', value: 2 },
      ], result: 'COMPLIANT' },
      { condition: 'includes', questionId: 'q1', value: ['nursing'], result: 'AT_RISK' },
      { condition: 'notIncludes', questionId: 'q1', value: ['nursing'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Agency provides required skilled services per RSMo 197.400.',
      atRisk: 'Agency must provide at least two skilled services. Add a second skilled service.',
      nonCompliant: 'Skilled nursing is required for Missouri home health licensure per RSMo 197.400.',
      unknown: 'Verify skilled services offered meet RSMo 197.400 requirements.',
    },
    stateNotes: [
      'Must offer 2+ skilled services on an intermittent basis',
      'One service must be skilled nursing',
      'Other services: PT, OT, ST, MSW, HHA',
    ],
  },

  {
    id: 'MO-A5',
    itemId: 'A5',
    stateCode: 'MO',
    title: 'Medicare Conditions of Participation Compliance',
    description: 'Missouri adopts federal Medicare CoPs by reference for all licensed home health agencies',
    severity: 'high',
    regulationCode: '19 CSR 30-26.010(1)(A)',
    regulationName: 'Medicare CoPs',
    agencyName: 'Missouri Department of Health and Senior Services',
    agencyAbbr: 'DHSS',
    agencyPhone: '(573) 751-6336',
    agencyUrl: 'https://health.mo.gov/safety/homecare/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency maintain compliance with the Medicare Conditions of Participation (42 CFR 484)?',
        inputType: 'select',
        options: [
          { value: 'certified', label: 'Yes - Medicare certified and in good standing' },
          { value: 'compliant', label: 'Yes - compliant but not Medicare certified' },
          { value: 'partial', label: 'Partially compliant' },
          { value: 'no', label: 'No / Not tracking' },
        ],
        required: true,
        helpText: 'Per 19 CSR 30-26.010(1)(A): Missouri incorporates 42 CFR 484 by reference. State surveys are conducted per Medicare standards.',
      },
    ],
    scoringRules: [
      { condition: 'includes', value: ['certified', 'compliant'], result: 'COMPLIANT' },
      { condition: 'equals', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Agency maintains Medicare CoP compliance as required by 19 CSR 30-26.010(1)(A).',
      atRisk: 'Partial compliance with Medicare CoPs puts your state license at risk. Address all gaps immediately.',
      nonCompliant: 'Missouri requires strict compliance with Medicare CoPs per 19 CSR 30-26.010(1)(A). Surveys are conducted per Medicare standards.',
      unknown: 'Review 42 CFR 484 requirements and ensure compliance.',
    },
    stateNotes: [
      'Missouri incorporates 42 CFR 484 (Medicare CoPs) by reference',
      'State licensure surveys conducted per Medicare standards',
      'All operational requirements follow federal CoPs',
    ],
  },

  {
    id: 'MO-A6',
    itemId: 'A6',
    stateCode: 'MO',
    title: 'Ownership Disclosure',
    description: 'Missouri requires disclosure of ownership and control interests',
    severity: 'medium',
    regulationCode: '19 CSR 30-26.010(2)(B)(1)',
    regulationName: 'Ownership',
    agencyName: 'Missouri Department of Health and Senior Services',
    agencyAbbr: 'DHSS',
    agencyPhone: '(573) 751-6336',
    agencyUrl: 'https://health.mo.gov/safety/homecare/',
    questions: [
      {
        id: 'q1',
        question: 'Have you submitted a current State Disclosure of Ownership and Control Interest Statement to DHSS?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required with initial application and any changes per 19 CSR 30-26.010(2)(B)(1).',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Ownership disclosure is on file with DHSS.',
      atRisk: 'Submit State Disclosure of Ownership and Control Interest Statement to DHSS.',
      nonCompliant: 'Ownership disclosure is required per 19 CSR 30-26.010(2)(B)(1).',
      unknown: 'Verify ownership disclosure status with DHSS.',
    },
    stateNotes: [
      'Required with initial application',
      'Must report changes with renewal application',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL & BACKGROUND CHECKS
  // ============================================
  {
    id: 'MO-B1',
    itemId: 'B1',
    stateCode: 'MO',
    title: 'Family Care Safety Registry (FCSR)',
    description: 'Missouri requires FCSR registration and background screening for home health employees',
    severity: 'high',
    regulationCode: 'RSMo 192.2495',
    regulationName: 'FCSR',
    agencyName: 'DHSS - Family Care Safety Registry',
    agencyAbbr: 'FCSR',
    agencyPhone: '(866) 422-6872',
    agencyUrl: 'https://health.mo.gov/safety/fcsr/',
    questions: [
      {
        id: 'q1',
        question: 'Are ALL employees registered with the Family Care Safety Registry?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees are registered' },
          { value: 'most', label: 'Most employees, some pending' },
          { value: 'no', label: 'No / Not tracking' },
        ],
        required: true,
        helpText: 'Per RSMo 192.2495: Employees of licensed home health agencies must register with FCSR regardless of hire date.',
      },
      {
        id: 'q2',
        question: 'Do you obtain FCSR background screening reports on all employees before they provide patient care?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - always before patient contact' },
          { value: 'no', label: 'No - sometimes start before screening' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'FCSR registration and screening meets Missouri requirements.',
      atRisk: 'Some employees may not have complete FCSR registration/screening. Verify all employees are registered and screened before patient contact.',
      nonCompliant: 'FCSR registration is mandatory per RSMo 192.2495. Employees must register within 15 days of hire. Failure to register is a Class B misdemeanor.',
      unknown: 'Contact FCSR at (866) 422-6872 to verify employee registration status.',
    },
    stateNotes: [
      'All home health employees must register with FCSR per RSMo 192.2495',
      'Registration required regardless of hire date',
      'Employees must register within 15 days of beginning employment',
      'Failure to register is Class B misdemeanor',
      'FCSR fee is $15 for workers',
    ],
  },

  {
    id: 'MO-B2',
    itemId: 'B2',
    stateCode: 'MO',
    title: 'Employee Disqualification List (EDL)',
    description: 'Missouri requires EDL verification for all employees',
    severity: 'high',
    regulationCode: 'RSMo 660.315',
    regulationName: 'EDL',
    agencyName: 'Missouri Department of Health and Senior Services',
    agencyAbbr: 'DHSS',
    agencyPhone: '(573) 522-1119',
    agencyUrl: 'https://health.mo.gov/safety/edl/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the Employee Disqualification List (EDL) for all potential employees before hire?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before hire' },
          { value: 'sometimes', label: 'Sometimes, not consistently' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'The EDL is part of the FCSR screening. Persons on EDL cannot be employed.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'EDL verification is completed for all employees.',
      atRisk: 'Implement consistent EDL checking for all hires.',
      nonCompliant: 'Hiring individuals on the EDL violates Missouri law. Good Cause Waivers cannot be obtained for EDL listings.',
      unknown: 'Verify EDL checking procedures.',
    },
    stateNotes: [
      'EDL is part of FCSR background screening',
      'No Good Cause Waiver available for EDL listings',
      'Contact EDL unit at (573) 522-1119',
    ],
  },

  {
    id: 'MO-B3',
    itemId: 'B3',
    stateCode: 'MO',
    title: 'Criminal Background Check (RSMo 660.317)',
    description: 'Missouri requires criminal background checks per RSMo 660.317',
    severity: 'high',
    regulationCode: 'RSMo 660.317',
    regulationName: 'Background Checks',
    agencyName: 'Missouri Department of Health and Senior Services',
    agencyAbbr: 'DHSS',
    agencyPhone: '(573) 751-6336',
    agencyUrl: 'https://health.mo.gov/safety/homecare/',
    questions: [
      {
        id: 'q1',
        question: 'Do you obtain criminal background checks through FCSR for all employees?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, for all employees before hire' },
          { value: 'some', label: 'For some employees' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'FCSR screening includes Missouri criminal background check.',
      },
      {
        id: 'q2',
        question: 'Do any employees have disqualifying offenses that would require a Good Cause Waiver?',
        inputType: 'select',
        options: [
          { value: 'none', label: 'No employees have disqualifying offenses' },
          { value: 'waiver_obtained', label: 'Yes, but waivers have been obtained' },
          { value: 'waiver_pending', label: 'Yes, waivers are pending' },
          { value: 'no_waiver', label: 'Yes, and no waiver obtained' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['none', 'waiver_obtained'] },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'some' },
        { condition: 'equals', questionId: 'q2', value: 'waiver_pending' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no_waiver' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Criminal background check procedures meet Missouri requirements.',
      atRisk: 'Background check procedures need improvement. Obtain waivers for employees with disqualifying offenses.',
      nonCompliant: 'Criminal background checks are required per RSMo 660.317. Good Cause Waivers must be obtained for disqualifying offenses.',
      unknown: 'Review RSMo 660.317 requirements for background checks.',
    },
    stateNotes: [
      'Criminal background included in FCSR screening',
      'Good Cause Waiver available through 19 CSR 30-82.060 for certain offenses',
      'No waiver available for EDL listings',
    ],
  },

  {
    id: 'MO-B4',
    itemId: 'B4',
    stateCode: 'MO',
    title: 'OIG/SAM Exclusion Verification',
    description: 'Federal exclusion list verification required for Medicare/Medicaid providers',
    severity: 'high',
    regulationCode: '42 CFR 1001.1901',
    regulationName: 'Exclusion List',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://oig.hhs.gov/exclusions/',
    questions: [
      {
        id: 'q1',
        question: 'How often do you check the OIG exclusion list for employees?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'Not checking' },
        ],
        required: true,
        helpText: 'OIG recommends monthly verification for all employees.',
      },
      {
        id: 'q2',
        question: 'Do you check SAM.gov exclusions?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'monthly' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Federal exclusion list verification is performed regularly.',
      atRisk: 'Implement monthly OIG/SAM verification. At-hire only is insufficient.',
      nonCompliant: 'Employing excluded individuals can result in civil monetary penalties and program exclusion.',
      unknown: 'Verify OIG/SAM checking procedures.',
    },
    stateNotes: [
      'OIG recommends monthly verification',
      'SAM.gov verification also recommended',
      'Employing excluded individuals results in severe penalties',
    ],
  },

  {
    id: 'MO-B5',
    itemId: 'B5',
    stateCode: 'MO',
    title: 'Personnel File Requirements (42 CFR 484)',
    description: 'Personnel file documentation per Medicare CoPs incorporated by Missouri',
    severity: 'high',
    regulationCode: '42 CFR 484.80',
    regulationName: 'Personnel',
    agencyName: 'Missouri Department of Health and Senior Services',
    agencyAbbr: 'DHSS',
    agencyPhone: '(573) 751-6336',
    agencyUrl: 'https://health.mo.gov/safety/homecare/',
    questions: [
      {
        id: 'q1',
        question: 'Which documents do you maintain in EVERY employee\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application' },
          { value: 'license', label: 'Professional license verification (RN, LPN, PT, OT, ST, MSW)' },
          { value: 'fcsr', label: 'FCSR background screening results' },
          { value: 'edl', label: 'EDL verification' },
          { value: 'oig', label: 'OIG exclusion check' },
          { value: 'competency', label: 'Competency evaluation' },
          { value: 'training', label: 'Orientation and training records' },
          { value: 'hha_cert', label: 'Home Health Aide competency evaluation (if applicable)' },
          { value: 'job_desc', label: 'Signed job description' },
          { value: 'health', label: 'Health screening/TB clearance' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain required documentation per 42 CFR 484 and Missouri requirements.',
      atRisk: 'Some personnel files may be incomplete. Missing: {missing}.',
      nonCompliant: 'Personnel files are missing critical documentation. Surveyors will cite incomplete files.',
      unknown: 'Audit personnel files against 42 CFR 484 requirements.',
    },
    stateNotes: [
      'Missouri follows 42 CFR 484 for personnel requirements',
      'FCSR and EDL documentation required for all employees',
      'Professional licenses must be current and verified',
    ],
  },

  // ============================================
  // SECTION C: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'MO-C1',
    itemId: 'C1',
    stateCode: 'MO',
    title: 'Dementia Training Requirements',
    description: 'Missouri requires dementia-specific training for home health employees',
    severity: 'high',
    regulationCode: 'RSMo 660.050',
    regulationName: 'Dementia Training',
    agencyName: 'Missouri Department of Health and Senior Services',
    agencyAbbr: 'DHSS',
    agencyPhone: '(573) 751-6336',
    agencyUrl: 'https://health.mo.gov/safety/homecare/',
    questions: [
      {
        id: 'q1',
        question: 'For staff providing DIRECT CARE to dementia patients, does training cover all required topics?',
        inputType: 'checklist',
        options: [
          { value: 'overview', label: 'Overview of Alzheimer\'s disease and related dementias' },
          { value: 'communication', label: 'Communicating with persons with dementia' },
          { value: 'behavior', label: 'Behavior management' },
          { value: 'adl', label: 'Promoting independence in activities of daily living' },
          { value: 'family', label: 'Understanding and dealing with family issues' },
        ],
        required: true,
        helpText: 'Per 19 CSR 30-26.010(1)(B)(1): All 5 topics required for direct care staff.',
      },
      {
        id: 'q2',
        question: 'Is dementia training provided during orientation and annually thereafter?',
        inputType: 'select',
        options: [
          { value: 'orientation_annual', label: 'Yes - during orientation and annually' },
          { value: 'orientation_only', label: 'During orientation only' },
          { value: 'no', label: 'No formal program' },
        ],
        required: true,
        helpText: 'Per 19 CSR 30-26.010(1)(B)(3): Training shall be incorporated into orientation and provided annually.',
      },
      {
        id: 'q3',
        question: 'Is training provided by a qualified instructor with education/experience in dementia care?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Unsure' },
        ],
        helpText: 'Per 19 CSR 30-26.010(1)(B)(3): Trainer must be qualified by education, experience, and knowledge.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'equals', questionId: 'q2', value: 'orientation_annual' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 3 },
        { condition: 'equals', questionId: 'q2', value: 'orientation_only' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Dementia training meets Missouri requirements per RSMo 660.050 and 19 CSR 30-26.010(1)(B).',
      atRisk: 'Dementia training program needs improvement. Ensure all 5 required topics are covered and training is provided annually.',
      nonCompliant: 'Dementia training is required by Missouri law. Implement training program per 19 CSR 30-26.010(1)(B) immediately.',
      unknown: 'Review dementia training requirements under RSMo 660.050.',
    },
    stateNotes: [
      'Direct care staff require training on all 5 topics',
      'Non-direct care staff with daily contact require 2 topics: overview and communication',
      'Training required during orientation and annually',
      'Trainer must be qualified by education and experience',
    ],
  },

  {
    id: 'MO-C2',
    itemId: 'C2',
    stateCode: 'MO',
    title: 'Home Health Aide Training and Competency',
    description: 'Home Health Aide requirements per Medicare CoPs incorporated by Missouri',
    severity: 'high',
    regulationCode: '42 CFR 484.80',
    regulationName: 'HHA Training',
    agencyName: 'Missouri Department of Health and Senior Services',
    agencyAbbr: 'DHSS',
    agencyPhone: '(573) 751-6336',
    agencyUrl: 'https://health.mo.gov/safety/homecare/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL home health aides meet one of the following qualifications?',
        inputType: 'checklist',
        options: [
          { value: 'training', label: 'Completed 75-hour training program meeting 42 CFR 484.80 requirements' },
          { value: 'competency', label: 'Passed HHA competency evaluation (written and skills demonstration)' },
          { value: 'cna', label: 'Current Missouri CNA certification' },
          { value: 'state_approved', label: 'Completed DHSS-approved training' },
        ],
        required: true,
        helpText: 'Per 42 CFR 484.80: HHAs must complete training or pass competency evaluation.',
      },
      {
        id: 'q2',
        question: 'Do you maintain competency evaluation documentation for all HHAs?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q3',
        question: 'Do HHAs receive 12 hours of in-service training annually?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HHAs complete 12+ hours' },
          { value: 'some', label: 'Some HHAs may be behind' },
          { value: 'no', label: 'No annual training program' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 1 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'all' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'some' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Home Health Aide training and competency meets 42 CFR 484.80 requirements.',
      atRisk: 'Some HHA training or documentation may be incomplete.',
      nonCompliant: 'HHA training program does not meet federal requirements incorporated by Missouri.',
      unknown: 'Review 42 CFR 484.80 requirements for HHA qualifications.',
    },
    stateNotes: [
      'Missouri follows 42 CFR 484.80 for HHA requirements',
      '75-hour training program or competency evaluation required',
      '12 hours annual in-service training required',
      'Contact DHSS at (573) 751-6336 for HHA competency evaluation information',
    ],
  },

  {
    id: 'MO-C3',
    itemId: 'C3',
    stateCode: 'MO',
    title: 'Infection Control Training',
    description: 'Infection control training per Medicare CoPs',
    severity: 'medium',
    regulationCode: '42 CFR 484.70',
    regulationName: 'Infection Control',
    agencyName: 'Missouri Department of Health and Senior Services',
    agencyAbbr: 'DHSS',
    agencyPhone: '(573) 751-6336',
    agencyUrl: 'https://health.mo.gov/safety/homecare/',
    questions: [
      {
        id: 'q1',
        question: 'Do all staff receive infection control training?',
        inputType: 'select',
        options: [
          { value: 'orientation_annual', label: 'Yes, during orientation and annually' },
          { value: 'orientation_only', label: 'During orientation only' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your agency maintain written infection control policies?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'orientation_annual' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'orientation_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control training and policies meet requirements.',
      atRisk: 'Provide infection control training annually, not just at orientation.',
      nonCompliant: 'Infection control training is required per 42 CFR 484.70.',
      unknown: 'Review infection control requirements.',
    },
    stateNotes: [
      'Follows 42 CFR 484.70 infection control requirements',
      'Written policies required',
      'Annual training recommended',
    ],
  },

  // ============================================
  // SECTION D: CARE DELIVERY
  // ============================================
  {
    id: 'MO-D1',
    itemId: 'D1',
    stateCode: 'MO',
    title: 'Plan of Care Requirements',
    description: 'Plan of Care requirements per Medicare CoPs incorporated by Missouri',
    severity: 'high',
    regulationCode: '42 CFR 484.60',
    regulationName: 'Care Planning',
    agencyName: 'Missouri Department of Health and Senior Services',
    agencyAbbr: 'DHSS',
    agencyPhone: '(573) 751-6336',
    agencyUrl: 'https://health.mo.gov/safety/homecare/',
    questions: [
      {
        id: 'q1',
        question: 'Is a written Plan of Care established for every patient before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are Plans of Care signed by a physician?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Are Plans of Care reviewed and updated at least every 60 days?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, every 60 days or more frequently' },
          { value: 'sometimes', label: 'Sometimes, not consistently' },
          { value: 'no', label: 'No regular review' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'equals', questionId: 'q3', value: 'sometimes' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plan of Care processes meet 42 CFR 484.60 requirements.',
      atRisk: 'Plan of Care processes need improvement. Ensure POCs are established before services and reviewed every 60 days.',
      nonCompliant: 'Plans of Care must be physician-signed and established before services per 42 CFR 484.60.',
      unknown: 'Review Plan of Care requirements under 42 CFR 484.60.',
    },
    stateNotes: [
      'Missouri follows 42 CFR 484.60 for POC requirements',
      'POC must be established before services begin',
      'Physician signature required',
      'Review at least every 60 days',
    ],
  },

  {
    id: 'MO-D2',
    itemId: 'D2',
    stateCode: 'MO',
    title: 'Supervision of Home Health Aides',
    description: 'HHA supervision requirements per Medicare CoPs',
    severity: 'high',
    regulationCode: '42 CFR 484.80(h)',
    regulationName: 'HHA Supervision',
    agencyName: 'Missouri Department of Health and Senior Services',
    agencyAbbr: 'DHSS',
    agencyPhone: '(573) 751-6336',
    agencyUrl: 'https://health.mo.gov/safety/homecare/',
    questions: [
      {
        id: 'q1',
        question: 'Does an RN (or appropriate therapist) conduct supervisory visits for HHA services?',
        inputType: 'select',
        options: [
          { value: 'every_14', label: 'Yes, at least every 14 days' },
          { value: 'monthly', label: 'Monthly' },
          { value: 'rarely', label: 'Rarely or as needed' },
          { value: 'never', label: 'No supervisory visits' },
        ],
        required: true,
        helpText: 'Per 42 CFR 484.80(h): RN or therapist must make supervisory visit every 14 days.',
      },
      {
        id: 'q2',
        question: 'Are supervisory visits conducted in the patient\'s home with the HHA present?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'every_14' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'monthly', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['rarely', 'never'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HHA supervision meets 42 CFR 484.80(h) requirements.',
      atRisk: 'HHA supervision frequency is insufficient. Federal requirement is every 14 days.',
      nonCompliant: 'HHA supervisory visits are required at least every 14 days per 42 CFR 484.80(h).',
      unknown: 'Review HHA supervision requirements.',
    },
    stateNotes: [
      'Supervisory visit every 14 days per 42 CFR 484.80(h)',
      'RN or therapist must conduct visit',
      'Visit must be in patient home with HHA present',
    ],
  },

  {
    id: 'MO-D3',
    itemId: 'D3',
    stateCode: 'MO',
    title: 'Patient Rights',
    description: 'Patient rights requirements per Medicare CoPs',
    severity: 'high',
    regulationCode: '42 CFR 484.50',
    regulationName: 'Patient Rights',
    agencyName: 'Missouri Department of Health and Senior Services',
    agencyAbbr: 'DHSS',
    agencyPhone: '(573) 751-6336',
    agencyUrl: 'https://health.mo.gov/safety/homecare/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written notice of patient rights before or during the initial visit?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does the patient rights notice include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'informed', label: 'Right to be informed of rights' },
          { value: 'participate', label: 'Right to participate in care planning' },
          { value: 'informed_advance', label: 'Right to be informed in advance of care' },
          { value: 'refuse', label: 'Right to refuse care' },
          { value: 'privacy', label: 'Right to privacy and confidentiality' },
          { value: 'complaint', label: 'Right to file complaints (including state hotline)' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 6 },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights notice meets 42 CFR 484.50 requirements.',
      atRisk: 'Patient rights notice may be incomplete. Include all required elements.',
      nonCompliant: 'Written patient rights notice is required per 42 CFR 484.50.',
      unknown: 'Review patient rights requirements.',
    },
    stateNotes: [
      'Written notice required before or during initial visit',
      'Must include state complaint hotline: (800) 392-0210',
      'All elements of 42 CFR 484.50 must be included',
    ],
  },

  // ============================================
  // SECTION E: DOCUMENTATION & ADMINISTRATION
  // ============================================
  {
    id: 'MO-E1',
    itemId: 'E1',
    stateCode: 'MO',
    title: 'Clinical Record Requirements',
    description: 'Clinical record documentation per Medicare CoPs',
    severity: 'high',
    regulationCode: '42 CFR 484.110',
    regulationName: 'Clinical Records',
    agencyName: 'Missouri Department of Health and Senior Services',
    agencyAbbr: 'DHSS',
    agencyPhone: '(573) 751-6336',
    agencyUrl: 'https://health.mo.gov/safety/homecare/',
    questions: [
      {
        id: 'q1',
        question: 'Do clinical records contain all required documentation?',
        inputType: 'checklist',
        options: [
          { value: 'demographics', label: 'Patient identifying information' },
          { value: 'poc', label: 'Physician-signed Plan of Care' },
          { value: 'oasis', label: 'OASIS assessments (if applicable)' },
          { value: 'orders', label: 'Physician orders' },
          { value: 'notes', label: 'Visit notes for all services' },
          { value: 'discharge', label: 'Discharge summary' },
          { value: 'consents', label: 'Consents and acknowledgments' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How long do you retain clinical records after discharge?',
        inputType: 'select',
        options: [
          { value: '5plus', label: '5 years or longer' },
          { value: '3to4', label: '3-4 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Unsure' },
        ],
        helpText: 'Federal requirement is 5 years per 42 CFR 484.110.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q2', value: 'unsure', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 7 },
        { condition: 'equals', questionId: 'q2', value: '5plus' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'equals', questionId: 'q2', value: '3to4' },
      ], result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical record documentation meets 42 CFR 484.110 requirements.',
      atRisk: 'Clinical record documentation or retention may be incomplete.',
      nonCompliant: 'Clinical records are missing required elements per 42 CFR 484.110.',
      unknown: 'Review clinical record requirements.',
    },
    stateNotes: [
      'Follows 42 CFR 484.110 clinical record requirements',
      'Retain records for at least 5 years after discharge',
      'DHSS surveys verify compliance through record review',
    ],
  },

  {
    id: 'MO-E2',
    itemId: 'E2',
    stateCode: 'MO',
    title: 'QAPI Program',
    description: 'Quality Assessment and Performance Improvement per Medicare CoPs',
    severity: 'medium',
    regulationCode: '42 CFR 484.65',
    regulationName: 'QAPI',
    agencyName: 'Missouri Department of Health and Senior Services',
    agencyAbbr: 'DHSS',
    agencyPhone: '(573) 751-6336',
    agencyUrl: 'https://health.mo.gov/safety/homecare/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a QAPI program that includes:',
        inputType: 'checklist',
        options: [
          { value: 'program', label: 'Ongoing QAPI program' },
          { value: 'indicators', label: 'Quality indicators tracked' },
          { value: 'action', label: 'Performance improvement projects' },
          { value: 'governing', label: 'Governing body oversight' },
          { value: 'infection', label: 'Infection surveillance and prevention' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'QAPI program meets 42 CFR 484.65 requirements.',
      atRisk: 'QAPI program may be incomplete.',
      nonCompliant: 'QAPI program is required per 42 CFR 484.65.',
      unknown: 'Review QAPI requirements.',
    },
    stateNotes: [
      'QAPI program required per 42 CFR 484.65',
      'Must include infection surveillance',
      'Governing body oversight required',
    ],
  },

  {
    id: 'MO-E3',
    itemId: 'E3',
    stateCode: 'MO',
    title: 'Emergency Preparedness',
    description: 'Emergency preparedness per Medicare CoPs',
    severity: 'medium',
    regulationCode: '42 CFR 484.102',
    regulationName: 'Emergency Plan',
    agencyName: 'Missouri Department of Health and Senior Services',
    agencyAbbr: 'DHSS',
    agencyPhone: '(573) 751-6336',
    agencyUrl: 'https://health.mo.gov/safety/homecare/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have an emergency preparedness program that includes:',
        inputType: 'checklist',
        options: [
          { value: 'plan', label: 'Written emergency preparedness plan' },
          { value: 'policies', label: 'Emergency policies and procedures' },
          { value: 'communication', label: 'Communication plan' },
          { value: 'training', label: 'Staff training and testing' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness program meets 42 CFR 484.102 requirements.',
      atRisk: 'Emergency preparedness program may be incomplete.',
      nonCompliant: 'Emergency preparedness program is required per 42 CFR 484.102.',
      unknown: 'Review emergency preparedness requirements.',
    },
    stateNotes: [
      'Emergency preparedness required per 42 CFR 484.102',
      'Plan must address natural disasters and emergencies',
      'Annual training and testing required',
    ],
  },

  // ============================================
  // SECTION F: SURVEY & COMPLIANCE
  // ============================================
  {
    id: 'MO-F1',
    itemId: 'F1',
    stateCode: 'MO',
    title: 'Survey Readiness',
    description: 'Preparation for DHSS surveys conducted per Medicare standards',
    severity: 'high',
    regulationCode: '19 CSR 30-26.010(2)(E)',
    regulationName: 'Inspections',
    agencyName: 'Missouri Department of Health and Senior Services',
    agencyAbbr: 'DHSS',
    agencyPhone: '(573) 751-6336',
    agencyUrl: 'https://health.mo.gov/safety/homecare/',
    questions: [
      {
        id: 'q1',
        question: 'When was your last DHSS survey?',
        inputType: 'select',
        options: [
          { value: 'within_12', label: 'Within the last 12 months' },
          { value: '12_to_36', label: '12-36 months ago' },
          { value: 'over_36', label: 'More than 36 months ago' },
          { value: 'never', label: 'Never surveyed / New agency' },
        ],
        required: true,
        helpText: 'Per RSMo 197.415: Agencies <36 months old surveyed every 12 months; established agencies every 36 months.',
      },
      {
        id: 'q2',
        question: 'Were any deficiencies cited in your last survey?',
        inputType: 'select',
        options: [
          { value: 'none', label: 'No deficiencies' },
          { value: 'minor', label: 'Minor deficiencies (corrected)' },
          { value: 'major', label: 'Major deficiencies' },
          { value: 'na', label: 'N/A - not yet surveyed' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['within_12', '12_to_36'] },
        { condition: 'includes', questionId: 'q2', value: ['none', 'minor'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'major', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'over_36', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Survey history is satisfactory.',
      atRisk: 'Address any outstanding deficiencies. Prepare for upcoming survey.',
      nonCompliant: 'Major deficiencies must be corrected per 19 CSR 30-26.010(2)(E).',
      unknown: 'Contact DHSS to verify survey schedule.',
    },
    stateNotes: [
      'Surveys may be unannounced per 19 CSR 30-26.010(2)(E)(1)',
      'New agencies (<36 months): surveyed every 12 months',
      'Established agencies: surveyed every 36 months',
      'Deficiency response due within 10 calendar days',
      'Accreditation may substitute for state survey per RSMo 197.415.4',
    ],
  },

  {
    id: 'MO-F2',
    itemId: 'F2',
    stateCode: 'MO',
    title: 'Complaint Hotline Awareness',
    description: 'Knowledge of patient complaint procedures',
    severity: 'low',
    regulationCode: '19 CSR 30-26.010(2)(H)',
    regulationName: 'Complaints',
    agencyName: 'Missouri Department of Health and Senior Services',
    agencyAbbr: 'DHSS',
    agencyPhone: '(800) 392-0210',
    agencyUrl: 'https://health.mo.gov/safety/homecare/',
    questions: [
      {
        id: 'q1',
        question: 'Do patients receive information about how to file complaints with DHSS?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - included in patient rights' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'DHSS Home Health/Hospice Hotline: (800) 392-0210',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Complaint information is provided to patients.',
      atRisk: 'Include DHSS complaint hotline (800) 392-0210 in patient rights notice.',
      nonCompliant: 'Patients must be informed of right to file complaints.',
      unknown: 'Verify complaint information is provided.',
    },
    stateNotes: [
      'DHSS Home Health/Hospice Hotline: (800) 392-0210',
      'Include in patient rights notice',
      'DHSS investigates complaints per 19 CSR 30-26.010(2)(H)',
    ],
  },
];

export default MISSOURI_QUESTIONS;